import React, { useEffect, useState, useRef } from 'react';
import { useTour } from './TourProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useNavigate, useLocation } from 'react-router-dom';

export const TourOverlay: React.FC = () => {
  const { currentTour, currentStepIndex, isActive, nextStep, previousStep, endTour, skipTour } = useTour();
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [highlightPosition, setHighlightPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [dialogPosition, setDialogPosition] = useState<{ top?: string; left?: string; transform?: string }>({});
  const navigate = useNavigate();
  const location = useLocation();
  const highlightRef = useRef<HTMLDivElement>(null);

  const currentStep = currentTour && currentStepIndex >= 0 ? currentTour.steps[currentStepIndex] : null;

  useEffect(() => {
    if (!isActive || !currentStep) return;

    // Navigate if needed
    if (currentStep.route && location.pathname !== currentStep.route) {
      navigate(currentStep.route);
      // Wait for navigation before continuing
      return;
    }

    // Execute action if provided
    if (currentStep.action) {
      currentStep.action();
    }

    // Wait for DOM to update, then find and highlight element
    const timer = setTimeout(() => {
      let element: HTMLElement | null = null;

      // Try data-tour attribute first (remove brackets if present)
      const tourTarget = currentStep.target.replace(/^\[data-tour="(.+)"\]$/, '$1');
      element = document.querySelector(`[data-tour="${tourTarget}"]`) as HTMLElement;
      
      // Fallback to CSS selector (if target is a CSS selector)
      if (!element && currentStep.target.startsWith('[')) {
        element = document.querySelector(currentStep.target) as HTMLElement;
      } else if (!element) {
        element = document.querySelector(currentStep.target) as HTMLElement;
      }

      if (element) {
        console.log('[TourOverlay] Found element:', element, 'for target:', currentStep.target);
        setHighlightedElement(element);
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        
        // Position dialog near element
        const rect = element.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        if (spaceBelow > 200) {
          // Position below
          setDialogPosition({
            top: `${rect.bottom + 20}px`,
            left: `${Math.max(20, Math.min(rect.left, window.innerWidth - 420))}px`,
            transform: 'none',
          });
        } else if (spaceAbove > 200) {
          // Position above
          setDialogPosition({
            top: `${rect.top - 300}px`,
            left: `${Math.max(20, Math.min(rect.left, window.innerWidth - 420))}px`,
            transform: 'none',
          });
        } else {
          // Center if not enough space
          setDialogPosition({
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          });
        }
      } else {
        console.warn(`Tour step target not found: ${currentStep.target}`);
        // Center dialog if element not found
        setDialogPosition({
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        });
      }
    }, currentStep.route && location.pathname !== currentStep.route ? 800 : 100);

    return () => clearTimeout(timer);
  }, [isActive, currentStep, navigate, location.pathname]);

  // Update highlight position when element changes or scrolls
  useEffect(() => {
    if (!highlightedElement || !isActive) {
      setHighlightPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!highlightedElement) return;

      const rect = highlightedElement.getBoundingClientRect();
      setHighlightPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    };

    // Initial update
    updatePosition();
    
    // Update on scroll/resize
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    // Also update periodically in case of dynamic content
    const interval = setInterval(updatePosition, 100);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearInterval(interval);
    };
  }, [highlightedElement, isActive]);

  if (!isActive || !currentStep || !currentTour) {
    return null;
  }

  const progress = ((currentStepIndex + 1) / currentTour.steps.length) * 100;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === currentTour.steps.length - 1;

  return (
    <>
      {/* Overlay backdrop - only show when we have a highlight */}
      {highlightPosition ? (
        <>
          {/* Top overlay */}
          {highlightPosition.y > 0 && (
            <div
              className="fixed z-[9998] pointer-events-none bg-black/40"
              style={{
                left: 0,
                top: 0,
                right: 0,
                height: `${highlightPosition.y}px`,
              }}
            />
          )}
          {/* Bottom overlay */}
          {highlightPosition.y + highlightPosition.height < window.innerHeight && (
            <div
              className="fixed z-[9998] pointer-events-none bg-black/40"
              style={{
                left: 0,
                top: `${highlightPosition.y + highlightPosition.height}px`,
                right: 0,
                bottom: 0,
              }}
            />
          )}
          {/* Left overlay */}
          {highlightPosition.x > 0 && (
            <div
              className="fixed z-[9998] pointer-events-none bg-black/40"
              style={{
                left: 0,
                top: `${highlightPosition.y}px`,
                width: `${highlightPosition.x}px`,
                height: `${highlightPosition.height}px`,
              }}
            />
          )}
          {/* Right overlay */}
          {highlightPosition.x + highlightPosition.width < window.innerWidth && (
            <div
              className="fixed z-[9998] pointer-events-none bg-black/40"
              style={{
                left: `${highlightPosition.x + highlightPosition.width}px`,
                top: `${highlightPosition.y}px`,
                right: 0,
                height: `${highlightPosition.height}px`,
              }}
            />
          )}
        </>
      ) : (
        <div className="fixed inset-0 z-[9998] pointer-events-none bg-black/40" />
      )}
      
      {/* Highlight border - ensure element stays bright */}
      {highlightedElement && highlightPosition && (
        <>
          {/* Ensure highlighted element is above overlay */}
          <div
            ref={highlightRef}
            className="fixed z-[10000] pointer-events-none transition-all duration-300"
            style={{
              left: `${highlightPosition.x}px`,
              top: `${highlightPosition.y}px`,
              width: `${highlightPosition.width}px`,
              height: `${highlightPosition.height}px`,
              border: '4px solid hsl(var(--primary))',
              borderRadius: '0.5rem',
              boxShadow: `
                0 0 0 4px hsl(var(--primary)),
                0 0 30px rgba(147, 51, 234, 0.6),
                inset 0 0 20px rgba(147, 51, 234, 0.2)
              `,
            }}
          />
          {/* Add a bright backdrop behind the element to ensure it's visible */}
          <div
            className="fixed z-[9999] pointer-events-none transition-all duration-300"
            style={{
              left: `${Math.max(0, highlightPosition.x - 4)}px`,
              top: `${Math.max(0, highlightPosition.y - 4)}px`,
              width: `${highlightPosition.width + 8}px`,
              height: `${highlightPosition.height + 8}px`,
              borderRadius: '0.5rem',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'brightness(1.2)',
            }}
          />
        </>
      )}

      {/* Tour Dialog */}
      <Dialog open={isActive} onOpenChange={() => skipTour()}>
        <DialogContent 
          className="max-w-md z-[10000] pointer-events-auto"
          style={{
            position: 'fixed',
            ...dialogPosition,
            margin: 0,
          }}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <DialogTitle>{currentStep.title}</DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={skipTour}
                className="h-6 w-6"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <DialogDescription className="pt-2">
              {currentStep.content}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Step {currentStepIndex + 1} of {currentTour.steps.length}
            </p>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {!isFirstStep && (
                <Button variant="outline" onClick={previousStep}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={skipTour}>
                Skip Tour
              </Button>
              <Button onClick={isLastStep ? endTour : nextStep}>
                {isLastStep ? 'Finish' : 'Next'}
                {!isLastStep && <ChevronRight className="h-4 w-4 ml-2" />}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

